/**
 * React-tree bootstrap shared by every entry point — `main.tsx` for the full
 * web app and the two prebundled viewer entries (`screen-viewer-entry.tsx`,
 * `transcript-viewer-entry.tsx`) that the mobile shell embeds.
 *
 * Each entry previously re-implemented the same provider chain
 * (`trpc.Provider` → `QueryClientProvider` → router). They diverged on the
 * router kind (`BrowserRouter` vs `MemoryRouter`) and on per-query defaults,
 * but everything between those points was duplicate. `PolygrafRoot` captures
 * the duplicate part and accepts those differences as props.
 *
 * Outer scaffolding (`StrictMode`, the Sentry error boundary) stays in
 * `main.tsx` only — the viewer entries don't want it.
 */

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createContext, type ReactNode, useContext } from 'react'
import { BrowserRouter, MemoryRouter } from 'react-router-dom'
import { applyNativeTextScale } from './lib/native-text-scale'
import { trpc, trpcClient } from './trpc/client'

// Every entry imports this module before its first render, and the native
// bridge is injected before any script runs, so text is sized from the first
// paint on (no flash at the web size inside the app).
applyNativeTextScale()

export type RouterDescriptor = { kind: 'browser' } | { kind: 'memory'; initialPath: string }

/**
 * True when the tree is a prebundled viewer — a `MemoryRouter` mounting the
 * single `/view/...` route its entry declares, with no app shell behind it.
 *
 * Read it before rendering a `<Link>` that leaves the viewer (`/`, `/auth`,
 * `/spaces/...`). Here those targets match no route, so following one unmounts
 * the page and leaves a blank WebView instead of navigating (#647).
 *
 * This is deliberately NOT `isNativeWebView()`. Both are true in the bundled
 * viewers, but `viewer.tsx` in the mobile app also injects `isNative: true`
 * while pointing a WebView at polygraf.app itself — the full `BrowserRouter`
 * app, where every one of those links resolves normally. Gating on native
 * would strip working navigation (notably the sign-in CTA an anonymous user
 * needs) from that case.
 *
 * Backed by the router kind, so any future MemoryRouter mount inherits it — a
 * preview harness or test wrapper would be classified standalone and silently
 * lose its navigation. Keep MemoryRouter and "prebundled viewer" synonymous in
 * this package, or promote this to an explicit prop.
 */
const StandaloneViewerContext = createContext(false)

export function useIsStandaloneViewer(): boolean {
	return useContext(StandaloneViewerContext)
}

interface PolygrafRootProps {
	router: RouterDescriptor
	queryClient: QueryClient
	children: ReactNode
}

/**
 * Wrap `children` in the canonical Polygraf provider chain. Use:
 *
 * ```tsx
 * <PolygrafRoot router={{ kind: 'browser' }} queryClient={queryClient}>
 *   <App />
 * </PolygrafRoot>
 * ```
 *
 * The router kind is the only structural variation between entries:
 * `browser` for the web SPA, `memory` for the prebundled viewers (so the
 * mobile WebView can mount them at a derived initial path without affecting
 * the browser URL).
 */
export function PolygrafRoot({ router, queryClient, children }: PolygrafRootProps) {
	const tree = (
		<trpc.Provider client={trpcClient} queryClient={queryClient}>
			<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
		</trpc.Provider>
	)

	if (router.kind === 'browser') {
		return (
			<StandaloneViewerContext.Provider value={false}>
				<BrowserRouter>{tree}</BrowserRouter>
			</StandaloneViewerContext.Provider>
		)
	}
	return (
		<StandaloneViewerContext.Provider value={true}>
			<MemoryRouter initialEntries={[router.initialPath]}>{tree}</MemoryRouter>
		</StandaloneViewerContext.Provider>
	)
}
